import React, { useState } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'

import type { HolidayListVariant } from '@/src/types/holidays'
import { HOLIDAYS_RADIUS } from '@/src/utils/holidays'

interface HolidayListGroup {
    title?: string
    variant: HolidayListVariant
    items: string[]
}

interface HolidayListProps {
    groups: HolidayListGroup[]
    footnotes: string[]
    /** Label for the collapsed state; takes a `count` of the hidden items. */
    showMoreLabel: (count: number) => string
    showLessLabel: string
    isMobile: boolean
}

/** A tiled list longer than this collapses: ten tiles is already a screenful on a phone. */
const COLLAPSE_AFTER = 10

/**
 * Inclusions and exclusions are what a traveller scans hardest, so each one is a tile with a
 * colour-coded badge rather than a line of body text: green tick for what is in the price, red
 * cross for what is not. Plain bullets keep the quieter treatment — they carry prose, not a
 * yes/no answer, and tiles would make a long notes list heavier than the thing it explains.
 */
const isTiled = (variant: HolidayListVariant) => variant === 'check' || variant === 'cross'

const BADGE = {
    check: { Icon: CheckRoundedIcon, color: 'success.main', bg: 'success.light' },
    cross: { Icon: CloseRoundedIcon, color: 'error.main', bg: 'error.light' }
} as const

const TiledItems: React.FC<{
    items: string[]
    variant: 'check' | 'cross'
    showMoreLabel: (count: number) => string
    showLessLabel: string
    isMobile: boolean
}> = ({ items, variant, showMoreLabel, showLessLabel, isMobile }) => {
    const { Icon, color, bg } = BADGE[variant]
    const [isExpanded, setIsExpanded] = useState(false)

    // Long inclusion and exclusion lists run to forty items and bury everything under them, so the
    // list opens on the first ten and the rest stay one tap away.
    const isCollapsible = items.length > COLLAPSE_AFTER
    const visible = isCollapsible && !isExpanded ? items.slice(0, COLLAPSE_AFTER) : items
    const hiddenCount = items.length - COLLAPSE_AFTER

    return (
        <Stack spacing={isMobile ? 1 : 1.25}>
            <Box
                component="ul"
                sx={{
                    m: 0,
                    p: 0,
                    listStyle: 'none',
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
                    gap: isMobile ? 1 : 1.25
                }}
            >
                {visible.map(item => (
                    <Stack
                        key={item}
                        component="li"
                        direction="row"
                        spacing={1.25}
                        sx={{
                            alignItems: 'flex-start',
                            p: isMobile ? 1.25 : 1.5,
                            borderRadius: HOLIDAYS_RADIUS.NESTED,
                            border: '1px solid',
                            borderColor: 'borders.default',
                            bgcolor: 'neutral.100'
                        }}
                    >
                        <Box
                            sx={{
                                width: 22,
                                height: 22,
                                flexShrink: 0,
                                mt: '1px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: bg,
                                color
                            }}
                        >
                            <Icon sx={{ fontSize: 15 }} />
                        </Box>

                        <Typography
                            sx={{
                                fontSize: isMobile ? '1rem' : '0.875rem',
                                lineHeight: 1.55,
                                color: 'textColors.subtitle'
                            }}
                        >
                            {item}
                        </Typography>
                    </Stack>
                ))}
            </Box>

            {isCollapsible && (
                <Button
                    variant="text"
                    onClick={() => setIsExpanded(current => !current)}
                    endIcon={isExpanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                    sx={{
                        alignSelf: 'flex-start',
                        px: 0,
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        '&:hover': { bgcolor: 'transparent' }
                    }}
                >
                    {isExpanded ? showLessLabel : showMoreLabel(hiddenCount)}
                </Button>
            )}
        </Stack>
    )
}

const PlainItems: React.FC<{ items: string[]; variant: HolidayListVariant; isMobile: boolean }> = ({
    items,
    variant,
    isMobile
}) => (
    <Box
        component="ul"
        sx={{
            m: 0,
            p: 0,
            listStyle: 'none',
            columnCount: isMobile ? 1 : 2,
            columnGap: 5
        }}
    >
        {items.map((item, index) => (
            <Box
                key={item}
                component="li"
                sx={{
                    display: 'flex',
                    gap: 1.25,
                    alignItems: 'flex-start',
                    breakInside: 'avoid',
                    mb: isMobile ? 1.25 : 1.5
                }}
            >
                {variant === 'numbered' ? (
                    <Typography
                        sx={{
                            minWidth: 20,
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            color: 'primary.main',
                            flexShrink: 0,
                            lineHeight: 1.6
                        }}
                    >
                        {index + 1}.
                    </Typography>
                ) : (
                    <FiberManualRecordIcon
                        sx={{ fontSize: 7, mt: '0.65em', flexShrink: 0, color: 'textColors.disabled' }}
                    />
                )}

                <Typography
                    sx={{
                        fontSize: isMobile ? '0.875rem' : '0.938rem',
                        lineHeight: 1.55,
                        color: 'textColors.subtitle'
                    }}
                >
                    {item}
                </Typography>
            </Box>
        ))}
    </Box>
)

const HolidayList: React.FC<HolidayListProps> = ({ groups, footnotes, showMoreLabel, showLessLabel, isMobile }) => {
    return (
        <Stack spacing={2.5}>
            {groups.map((group, groupIndex) => (
                <Box key={group.title ?? `group-${groupIndex}`}>
                    {group.title && (
                        <Typography
                            component="h3"
                            sx={{
                                fontSize: isMobile ? '0.938rem' : '1rem',
                                fontWeight: 700,
                                color: 'textColors.primary',
                                mb: 1.25
                            }}
                        >
                            {group.title}
                        </Typography>
                    )}

                    {isTiled(group.variant) ? (
                        <TiledItems
                            items={group.items}
                            variant={group.variant as 'check' | 'cross'}
                            showMoreLabel={showMoreLabel}
                            showLessLabel={showLessLabel}
                            isMobile={isMobile}
                        />
                    ) : (
                        <PlainItems items={group.items} variant={group.variant} isMobile={isMobile} />
                    )}
                </Box>
            ))}

            {footnotes.length > 0 && (
                <Stack spacing={1} sx={{ pt: 1.5, borderTop: '1px solid', borderColor: 'borders.default' }}>
                    {footnotes.map(footnote => (
                        <Typography
                            key={footnote}
                            sx={{ fontSize: '0.75rem', lineHeight: 1.7, color: 'textColors.subheading' }}
                        >
                            {footnote}
                        </Typography>
                    ))}
                </Stack>
            )}
        </Stack>
    )
}

export { HolidayList }
export type { HolidayListProps, HolidayListGroup }
export default HolidayList
